import { Component, forwardRef, inject, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator, Validators,
} from '@angular/forms';
import { ConfigurationFieldsAdapterService } from '../../../modules/sources/components/source-configuration-list/components/configuration-tab/configuration-fields-adapter.service';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { DataSourcesService } from '../../../modules/sources/services/data-sources.service';
import { VerticalDatumOption } from './vertical-datum-option';
import { SuggestDatumsModel } from '../../interfaces/suggest-datums-model';

@Component({
  selector: 'mee-vertical-datum',
  templateUrl: './vertical-datum.component.html',
  styleUrls: ['./vertical-datum.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => VerticalDatumComponent),
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => VerticalDatumComponent),
      multi: true,
    },
  ],
})
export class VerticalDatumComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy {
  verticalDatumControl = new FormControl(null);
  private configurationFieldsAdapterService = inject(ConfigurationFieldsAdapterService);
  private dataSourceService = inject(DataSourcesService);
  private destroy$ = new Subject<void>();
  private verticalDatumOptions: VerticalDatumOption[] = [];
  private initialValue: number;
  private espgCode$ = new BehaviorSubject<number | null>(null);
  isRequired = false;
  filteredDatum$: Observable<VerticalDatumOption[]>;

  @Input() placeholder = null;

  @Input() set espg(datum: number) {
    this.espgCode$.next(datum);
  }

  ngOnInit(): void {
    this.dataSourceService
      .getVerticalDatums()
      .pipe(
        tap(({ Datums }) => {
          this.verticalDatumOptions = this.configurationFieldsAdapterService.getVerticalDatumOptions(Datums);

          if (this.initialValue) {
            const initialDatum = this.verticalDatumOptions.find(d => d.value === this.initialValue);

            this.verticalDatumControl.setValue(initialDatum, { emitEvent: false });
          }

          this.initFilteredDatum();
        }),
        switchMap(() => this.espgCode$.asObservable()),
        filter(value => !!value),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(datum => this.getSuggestedDatumList(datum)),
        tap(({ SuggestedDatums }) => {
          const verticalsDatumsWithSuggest = this.verticalDatumOptions.map(verticalDatum => {
            const suggestedDatum = SuggestedDatums.find(sd => verticalDatum.value === sd.Epsg);

            if (suggestedDatum) {
              return { ...verticalDatum, isSuggest: true };
            }

            return { ...verticalDatum, isSuggest: false };
          });

          this.verticalDatumOptions = this.sortBySuggest(verticalsDatumsWithSuggest);

          this.initFilteredDatum();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.verticalDatumControl.valueChanges.subscribe(value => {
      if (typeof value === 'string' && value.length === 0) {
        this.verticalDatumControl.setValue(null);
        this.onChange(null);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onChange: any = () => {};

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.verticalDatumControl.disable();
    } else {
      this.verticalDatumControl.enable();
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    this.isRequired = control.hasValidator(Validators.required);

    return null;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {}

  writeValue(datum: number): void {
    this.initialValue = datum;

    if (this.verticalDatumOptions.length > 0) {
      const initialDatum = this.verticalDatumOptions.find(d => d.value === this.initialValue);

      this.verticalDatumControl.setValue(initialDatum, { emitEvent: true });
      this.initFilteredDatum();
    }
  }

  displayedDatum(datum: VerticalDatumOption): string {
    if (datum) {
      return `${datum.title} (${datum.value})`;
    }

    return '';
  }

  onSelectDatum({ option }: MatAutocompleteSelectedEvent) {
    const datum: VerticalDatumOption = option.value;
    this.onChange(datum.value);
  }

  getDatumTooltip(datum: VerticalDatumOption): string {
    return `${datum.title} (${datum.value}) ${datum.isSuggest ? '(Suggested)' : ''}`;
  }

  private getSuggestedDatumList(datum: number): Observable<SuggestDatumsModel> {
    if (!Number(datum)) {
      return of({ SuggestedDatums: [] });
    }

    return this.dataSourceService.getSuggestedVerticalDatums(datum);
  }

  private initFilteredDatum(): void {
    this.filteredDatum$ = this.verticalDatumControl.valueChanges.pipe(
      startWith(''),
      distinctUntilChanged(),
      map(value => this.filteredDatumList(value)),
    );
  }

  private filteredDatumList(datum: string): VerticalDatumOption[] {
    const filterValue = String(datum).toLowerCase();
    const filteredList = this.verticalDatumOptions.filter(d => {
      return d.title.toLowerCase().includes(filterValue) || String(d.value).toLowerCase().includes(filterValue);
    });

    return this.sortBySuggest(filteredList);
  }

  private sortBySuggest(list: VerticalDatumOption[]): VerticalDatumOption[] {
    return list.sort((a, b) => {
      if (a.isSuggest !== b.isSuggest) {
        return a.isSuggest ? -1 : 1;
      }
    });
  }
}
